import config from '../../config';
import { validFilterParams } from '../../containers/SearchPage/SearchPage.helpers';
import { convertUnitToSubUnit, unitDivisor } from '../currency';
import { formatDateStringToUTC, getExclusiveEndDate } from '../dates';

// Route planner reuses the exact same filter config as /s. Keeping a single
// source of truth means any filter added to SearchPage auto-flows into the
// planner without a second code path.
const FILTER_CONFIG = config.custom.filters;

const priceSearchParams = (priceParam) => {
  const inSubunits = (value) =>
    convertUnitToSubUnit(value, unitDivisor(config.currencyConfig.currency));
  const values = priceParam ? priceParam.split(',') : [];
  return priceParam && values.length === 2
    ? {
        price: [inSubunits(values[0]), inSubunits(values[1]) + 1].join(','),
      }
    : {};
};

const datesSearchParams = (datesParam) => {
  const values = datesParam ? datesParam.split(',') : [];
  const hasValues = datesParam && values.length === 2;
  const startDate = hasValues ? values[0] : null;
  const isNightlyBooking = config.bookingUnitType === 'line-item/night';
  const endDate =
    hasValues && isNightlyBooking
      ? values[1]
      : hasValues
        ? getExclusiveEndDate(values[1])
        : null;

  return hasValues
    ? {
        start: formatDateStringToUTC(startDate),
        end: formatDateStringToUTC(endDate),
        availability: 'full',
      }
    : {};
};

/**
 * Translate the route planner's Redux `filterParams` (same query-param keys
 * the SearchPage produces in the URL) into an SDK-ready query-param object.
 *
 * Mirrors the `searchListings` thunk in SearchPage.duck.js: price strings
 * become subunit ranges, date strings become UTC `start`/`end` + `availability`,
 * select filters are validated against `config.custom.filters`, and any key
 * not recognised is dropped. Invalid values inside known filters are silently
 * stripped by `validFilterParams`.
 *
 * @param {Record<string, string>} filterParams
 * @returns {Record<string, string>} SDK-shaped query params, ready to merge
 *   with bounds/per_page/fields on each tile query.
 */
export const buildSdkFilterParams = (filterParams) => {
  const validated = validFilterParams(filterParams || {}, FILTER_CONFIG);
  const { price, dates, plus_listing, no_plus_listing, ...rest } = validated;

  // Mirror SearchPage.duck.js: the Plus/non-Plus toggles are implemented as
  // hard-coded price ranges rather than a real listing flag, so they have to
  // override any user-selected price band before hitting the SDK. The values
  // here are already in SDK minor units, so they bypass priceSearchParams
  // (which would otherwise multiply by 100 again).
  let priceMaybe = priceSearchParams(price);
  if (Number(plus_listing) === 1) {
    priceMaybe = { price: '2001,10001' };
  } else if (Number(no_plus_listing) === 1) {
    priceMaybe = { price: '1,2000' };
  }

  return {
    ...rest,
    ...priceMaybe,
    ...datesSearchParams(dates),
  };
};

/**
 * Drop filter entries whose values are empty — a FilterComponent clears a
 * filter by submitting `{ queryParamName: null }`, and we want the resulting
 * Redux state to omit the key entirely so `Object.keys(filterParams).length`
 * remains a reliable "active filter count".
 *
 * @param {Record<string, unknown>} params
 * @returns {Record<string, string>}
 */
export const cleanFilterParams = (params) => {
  const out = {};
  for (const [key, value] of Object.entries(params || {})) {
    if (value === '' || value == null) continue;
    out[key] = value;
  }
  return out;
};
